import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { trackPromise } from 'react-promise-tracker';
import caAPI from '../../../../services/caAPI';
import ModalDommus from '../../../../components/Modal';
import { errorToast, successToast } from '../../../../components/DommusToast';
import { extrairDados, obterMensagemErro } from '../../../Configuracao/GestaoAgentesIa/utils';
import './style.scss';

/**
 * Fluxo de curadoria do atendente: a partir das mensagens marcadas no WhatsappComponent,
 * escolhe a base de aprendizado de destino, o tipo (bom/ruim) e a justificativa, gera a
 * prévia mascarada e só então envia o exemplo à base.
 *
 * O corpo enviado para a prévia e para o store é o MESMO objeto (`montarCorpo`): a prévia
 * é o que o atendente aprova, então o envio não pode divergir do que foi mostrado.
 */
function obterMensagemCampo(erro, campo, mensagemPadrao) {
  const mensagem = erro?.response?.data?.[campo];
  return Array.isArray(mensagem) ? mensagem[0] : mensagem || mensagemPadrao;
}

export default function CriarExemploAprendizado({ aberto, close, idAtendimento, idsMensagens, onEnviado }) {
  const [bases, setBases] = useState([]);
  const [idBaseConhecimento, setIdBaseConhecimento] = useState('');
  const [tipo, setTipo] = useState('bom');
  const [justificativa, setJustificativa] = useState('');
  const [conteudoMascarado, setConteudoMascarado] = useState('');
  const [previaGerada, setPreviaGerada] = useState(false);
  const [erroGeral, setErroGeral] = useState('');
  const [errosCampo, setErrosCampo] = useState({});
  const [enviando, setEnviando] = useState(false);

  // Reinicia o estado a cada abertura; carrega as bases marcadas como de aprendizado.
  useEffect(() => {
    if (!aberto) return;
    setIdBaseConhecimento('');
    setTipo('bom');
    setJustificativa('');
    setConteudoMascarado('');
    setPreviaGerada(false);
    setErroGeral('');
    setErrosCampo({});
    setEnviando(false);

    trackPromise(
      caAPI.baseConhecimento.list({ page: 1, per_page: 100 })
        .then(resposta => {
          const lista = (extrairDados(resposta) || []).filter(base => base.para_aprendizado === true);
          setBases(lista);
        })
        .catch(erro => setErroGeral(obterMensagemErro(erro, 'Erro ao carregar as bases de aprendizado.')))
    );
  }, [aberto]);

  // Qualquer alteração dos campos invalida a prévia: o envio precisa refletir o que foi aprovado.
  function invalidarPrevia() {
    setPreviaGerada(false);
    setConteudoMascarado('');
  }

  const semBases = bases.length === 0;

  const corpoRequisicao = useMemo(() => ({
    id_atendimento: idAtendimento,
    id_base_conhecimento: Number(idBaseConhecimento) || null,
    tipo,
    justificativa: justificativa.trim(),
    ids_mensagens: idsMensagens,
  }), [idAtendimento, idBaseConhecimento, tipo, justificativa, idsMensagens]);

  function tratarErroValidacao(erro, mensagemPadrao) {
    if (erro?.response?.status === 422) {
      const dados = erro.response.data || {};
      const novosErros = {};
      ['id_base_conhecimento', 'tipo', 'justificativa', 'ids_mensagens', 'id_atendimento'].forEach(campo => {
        const mensagem = obterMensagemCampo(erro, campo, '');
        if (mensagem) novosErros[campo] = mensagem;
      });
      setErrosCampo(novosErros);
      const mensagemGeral = dados.erro || dados.message || Object.values(novosErros)[0] || mensagemPadrao;
      errorToast.fire({ text: mensagemGeral });
      return;
    }
    errorToast.fire({ text: obterMensagemErro(erro, mensagemPadrao) });
  }

  function handleGerarPrevia() {
    if (!idBaseConhecimento) {
      setErrosCampo(erros => ({ ...erros, id_base_conhecimento: 'Selecione a base de destino.' }));
      return;
    }
    if (!justificativa.trim()) {
      setErrosCampo(erros => ({ ...erros, justificativa: 'Informe a justificativa.' }));
      return;
    }
    setErrosCampo({});
    trackPromise(
      caAPI.experienciaAprendizado.previa(corpoRequisicao)
        .then(resposta => {
          setConteudoMascarado(resposta?.data?.conteudo_mascarado ?? '');
          setPreviaGerada(true);
        })
        .catch(erro => {
          setPreviaGerada(false);
          tratarErroValidacao(erro, 'Erro ao gerar a prévia do exemplo.');
        })
    );
  }

  function handleEnviar() {
    if (!previaGerada) return;
    setEnviando(true);
    trackPromise(
      caAPI.experienciaAprendizado.store(corpoRequisicao)
        .then(() => {
          successToast.fire({ text: 'Exemplo enviado.' });
          onEnviado && onEnviado();
        })
        .catch(erro => tratarErroValidacao(erro, 'Erro ao enviar o exemplo à base.'))
        .finally(() => setEnviando(false))
    );
  }

  return (
    <ModalDommus open={aberto} close={close} titulo="Criar exemplo de aprendizado" size="lg">
      {erroGeral && <Alert variant="danger">{erroGeral}</Alert>}

      <Form.Group controlId="base-aprendizado-destino">
        <Form.Label>Base de aprendizado de destino</Form.Label>
        {semBases ? (
          <Alert variant="info" className="mb-0">
            Peça a um administrador para marcar uma base de aprendizado.
          </Alert>
        ) : (
          <>
            <Form.Control
              as="select"
              id="base-aprendizado-destino"
              value={idBaseConhecimento}
              onChange={evento => { setIdBaseConhecimento(evento.target.value); invalidarPrevia(); }}
              isInvalid={Boolean(errosCampo.id_base_conhecimento)}
            >
              <option value="">Selecione uma base</option>
              {bases.map(base => (
                <option key={base.id_base_conhecimento} value={base.id_base_conhecimento}>{base.nome}</option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">{errosCampo.id_base_conhecimento}</Form.Control.Feedback>
          </>
        )}
      </Form.Group>

      <Form.Group>
        <Form.Label>Tipo do exemplo</Form.Label>
        <div className="grupo-tipo-exemplo" role="group" aria-label="Tipo do exemplo">
          <Button
            type="button"
            id="tipo-exemplo-bom"
            variant={tipo === 'bom' ? 'success' : 'outline-success'}
            className="mr-2"
            onClick={() => { setTipo('bom'); invalidarPrevia(); }}
          >
            Bom exemplo
          </Button>
          <Button
            type="button"
            id="tipo-exemplo-ruim"
            variant={tipo === 'ruim' ? 'danger' : 'outline-danger'}
            onClick={() => { setTipo('ruim'); invalidarPrevia(); }}
          >
            Exemplo ruim
          </Button>
        </div>
      </Form.Group>

      <Form.Group controlId="justificativa-exemplo">
        <Form.Label>Justificativa</Form.Label>
        <Form.Control
          as="textarea"
          id="justificativa-exemplo"
          rows={3}
          value={justificativa}
          onChange={evento => { setJustificativa(evento.target.value); invalidarPrevia(); }}
          isInvalid={Boolean(errosCampo.justificativa)}
          required
        />
        <Form.Control.Feedback type="invalid">{errosCampo.justificativa}</Form.Control.Feedback>
      </Form.Group>

      <div className="mb-3">
        <Button
          type="button"
          id="gerar-previa-exemplo"
          variant="outline-primary"
          onClick={handleGerarPrevia}
          disabled={semBases}
        >
          Gerar prévia
        </Button>
      </div>

      {previaGerada && (
        <Form.Group>
          <Form.Label>Prévia (conteúdo mascarado)</Form.Label>
          <pre id="previa-exemplo" className="previa-exemplo-conteudo">{conteudoMascarado}</pre>
        </Form.Group>
      )}

      <div className="d-flex justify-content-end">
        <Button type="button" variant="secondary" className="mr-2" onClick={close}>Cancelar</Button>
        <Button
          type="button"
          id="enviar-exemplo-base"
          variant="success"
          onClick={handleEnviar}
          disabled={!previaGerada || enviando || semBases}
        >
          {enviando ? 'Enviando...' : 'Enviar à base'}
        </Button>
      </div>
    </ModalDommus>
  );
}
